/* eslint-disable import/order */
/* eslint-disable global-require */
import React, { useState, useEffect, useRef } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { IconButton, Button, makeStyles, Grid, Box } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import ptLocale from '@fullcalendar/core/locales/pt-br'

import {
  findAttendantes,
  getServicesBooked,
} from '../../callendar/CalendarActions'
import Chips from '../../callendar/pages/chips'
import { mdiCalendarMultiple, mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import Icon from '@mdi/react'
import colors from '../../../assets/colors'
import BaseFilterModal from '../../../components/hooks/BaseFilterModal'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

const useStyles = makeStyles({
  btnCalendar: {
    whiteSpace: 'nowrap',
    color: '#FFFF',
    fontSize: '14px',
    backgroundColor: colors.primary,
    borderRadius: '10px',
    padding: '4px 12px',
    transition: 'all .4s ease-in-out',
    '&:hover': {
      opacity: 0.6,
      backgroundColor: colors.primary,
    },
  },
  calendarButtons: {
    width: '80px',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'row',
    '& > :first-child': {
      borderRadius: '5px 0 0 5px',
    },
    '& > :last-child': {
      borderRadius: '0 5px 5px 0',
    },
  },
  calendarButton: {
    width: '50%',
    borderRadius: '0',
    border: `1px solid ${colors.graylight}`,
    padding: 0,
    transition: 'all .4s ease-in-out',
    '&:hover': {
      backgroundColor: 'inherit',
      opacity: '0.5',
    },
  },
  todayButton: {
    width: 'auto',
    height: '100%',
    padding: '0 12px',
    fontSize: '14px',
    borderRadius: '5px',
    border: `1px solid ${colors.graylight}`,
    transition: 'all .4s ease-in-out',
    '&:hover': {
      backgroundColor: 'inherit',
      opacity: '0.5',
    },
  },
  calendarHeaderTitle: {
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '16px',
    textAlign: 'center',
    color: colors.primary,
    textTransform: 'capitalize',
  },
  periodButtons: {
    width: '157px',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    '& > :first-child': {
      borderRadius: '5px 0 0 5px',
    },
    '& > :last-child': {
      borderRadius: '0 5px 5px 0',
    },
  },
  periodButton: {
    borderRadius: '0',
    height: '22px',
    fontSize: '14px',
    color: colors.gray,
    textTransform: 'capitalize',
    fontWeight: '400',
    border: `1px solid ${colors.graylight}`,
    transition: 'all .4s ease-in-out',
    '&:hover': {
      backgroundColor: 'inherit',
      opacity: '0.8',
    },
  },
  active: {
    backgroundColor: colors.primary,
    color: '#FFF',
    '&:hover': {
      backgroundColor: `${colors.primary} !important`,
      opacity: '0.8',
    },
  },
  // Com muitos atendentes os chips quebram em várias linhas e empurram o
  // calendário para fora do modal: limita a área e rola dentro dela.
  chipsWrapper: {
    maxHeight: '80px',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  calendarBody: {
    maxHeight: `calc(530px - 165px)`,
    height: '100%',
    overflowY: 'auto',
  },
})

function DialogCalendar(props) {
  const { classes } = props
  const styles = useStyles()

  const [open, setOpen] = useState(false)

  const dispatch = useDispatch()

  const [bookedEvents, setBookedEvents] = useState([])
  const [bookedFiltered, setBookedFiltered] = useState(bookedEvents)
  const [attendant, setAttendant] = useState('')
  const attendants = useSelector(state => state.calendar.attendants)

  const [view, setView] = useState('timeGridDay')
  const [date, setDate] = useState(new Date())

  const calendarComponentRef = useRef()

  if (typeof window !== 'undefined') {
    const { default: FullCalendar } = require('@fullcalendar/react')
    const { default: dayGridPlugin } = require('@fullcalendar/daygrid')
    const { default: timeGridPlugin } = require('@fullcalendar/timegrid')
    const { default: interactionPlugin } = require('@fullcalendar/interaction')

    const filterAttendant = id => {
      setAttendant(id)
    }

    const initialLoad = async () => {
      const respBooked = await dispatch(getServicesBooked(true))

      if (respBooked?.code === 200) {
        const { data } = respBooked

        setBookedEvents(data)
      }
    }

    useEffect(() => {
      if (!attendants.length) dispatch(findAttendantes())

      if (open === true) initialLoad()
    }, [open])

    useEffect(() => {
      if (window.Echo) {
        window.Echo.channel(`Calendar`).listen(
          `CalendarEventHasUpdatedEvent`,
          () => {
            initialLoad()
          }
        )
      }

      return () => window.Echo?.leaveChannel(`Calendar`)
    }, [])

    useEffect(() => {
      if (attendant) {
        setBookedFiltered(
          bookedEvents.filter(item => item.attendant?.id === attendant)
        )
      } else {
        setBookedFiltered(bookedEvents)
      }
    }, [bookedEvents, attendant])

    const handleClickOpen = () => {
      setOpen(true)
    }

    const handleClose = () => {
      setOpen(false)
    }

    const handleNextDay = () => {
      const calendarApi = calendarComponentRef.current.getApi()
      calendarApi.prev()

      const nextDate = new Date(calendarApi.getDate())
      setDate(nextDate)
    }

    const handlePrevDay = () => {
      const calendarApi = calendarComponentRef.current.getApi()
      calendarApi.next()

      const prevDate = new Date(calendarApi.getDate())
      setDate(prevDate)
    }

    const handleSetToday = () => {
      const dateToGo = new Date()
      setView('timeGridDay')
      setDate(dateToGo)

      const calendarApi = calendarComponentRef.current.getApi()
      calendarApi?.gotoDate(dateToGo)
    }

    const handleDateNavigation = date => {
      const dateToGo = new Date(date)
      setView('timeGridDay')
      setDate(dateToGo)

      const calendarApi = calendarComponentRef.current.getApi()
      calendarApi?.gotoDate(dateToGo)
    }

    return (
      <>
        <IconButton className={styles.btnCalendar} onClick={handleClickOpen}>
          <Icon
            path={mdiCalendarMultiple}
            size={1}
            style={{ marginRight: '4px' }}
          />
          Confira a agenda da semana
        </IconButton>

        <BaseFilterModal
          open={open}
          handleOpen={handleClose}
          title={'Calendário da semana'}
          hiddenHelpButton
        >
          <Grid item xs={12} style={{ width: '690px', height: '530px' }}>
            <Grid container spacing={2}>
              <Grid item xs>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item xs>
                    <Grid container spacing={2}>
                      <Grid item>
                        <Box className={styles.calendarButtons}>
                          <IconButton
                            className={styles.calendarButton}
                            onClick={handleNextDay}
                          >
                            <Icon path={mdiChevronLeft} size={1} />
                          </IconButton>

                          <IconButton
                            className={styles.calendarButton}
                            onClick={handlePrevDay}
                          >
                            <Icon path={mdiChevronRight} size={1} />
                          </IconButton>
                        </Box>
                      </Grid>
                      <Grid item>
                        <IconButton
                          className={styles.todayButton}
                          onClick={handleSetToday}
                        >
                          Hoje
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs>
                    <Box className={styles.calendarHeaderTitle}>
                      {format(date, "iiii, dd 'de' MMMM 'de' yyyy", {
                        locale: ptBR,
                      })}
                    </Box>
                  </Grid>

                  <Grid item xs container justifyContent="flex-end">
                    <Box className={styles.periodButtons}>
                      <IconButton
                        className={[
                          styles.periodButton,
                          view == 'dayGridMonth' && styles.active,
                        ]}
                        onClick={() => setView('dayGridMonth')}
                      >
                        Mês
                      </IconButton>

                      <IconButton
                        className={[
                          styles.periodButton,
                          view == 'timeGridWeek' && styles.active,
                        ]}
                        onClick={() => setView('timeGridWeek')}
                      >
                        Semana
                      </IconButton>

                      <IconButton
                        className={[
                          styles.periodButton,
                          view == 'timeGridDay' && styles.active,
                        ]}
                        onClick={() => setView('timeGridDay')}
                      >
                        Dia
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} className={styles.chipsWrapper}>
                <Chips
                  attendant={attendant}
                  classes={classes}
                  attendants={attendants}
                  filterAttendant={filterAttendant}
                />
              </Grid>

              <Grid item xs={12}>
                <div className={styles.calendarBody}>
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    // header controls
                    headerToolbar={{
                      left: '',
                      center: '',
                      right: '',
                    }}
                    // views and locale
                    key={`calendar-key-${view}`}
                    initialView={view}
                    locale={ptLocale}
                    editable={false}
                    initialDate={date}
                    droppable
                    navLinks
                    weekNumberCalculation="ISO"
                    eventDurationEditable={false}
                    selectMirror
                    dayMaxEvents={false}
                    dayMaxEventRows={false}
                    allDaySlot={false}
                    events={bookedFiltered}
                    // time
                    slotDuration="00:30:00"
                    slotLabelInterval="00:30:00"
                    slotMaxTime="24:00:00"
                    slotMinTime="00:00:00"
                    slotLabelFormat={{
                      hour: 'numeric',
                      minute: '2-digit',
                      omitZeroMinute: false,
                      meridiem: 'short',
                    }}
                    scrollTime="07:00:00"
                    ref={calendarComponentRef}
                    navLinkDayClick={handleDateNavigation}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>
        </BaseFilterModal>
        {/* <Dialog
          maxWidth="lg"
          open={open}
          onClose={handleClose}
          aria-labelledby="max-width-dialog-title"
        >
          <DialogTitle id="max-width-dialog-title">
            Calendário da semana
          </DialogTitle>
          <DialogContent>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Fechar
            </Button>
          </DialogActions>
        </Dialog> */}
      </>
    )
  }
}

export default React.memo(DialogCalendar)
